import React, { useState } from 'react';
import {
  Box,
  Heading,
  Text,
  Stack,
  Badge,
  Icon,
  VStack,
  HStack,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from '@chakra-ui/react';
import { FaRegCalendarAlt, FaTasks, FaFileAlt, FaChartLine, FaInfoCircle } from 'react-icons/fa';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register components of Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const PlanDeTrabajoPresidencia: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedTask, setSelectedTask] = useState<string | null>(null);

  const data = {
    labels: ['Tarea 1', 'Tarea 2', 'Tarea 3', 'Tarea 4'],
    datasets: [
      {
        label: 'Progreso (%)',
        data: [65, 59, 80, 81],
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        fill: true,
        tension: 0.1,
      },
    ],
  };

  const handleTaskClick = (task: string) => {
    setSelectedTask(task);
    onOpen();
  };

  return (
    <Box p={8} bg="gray.50" borderRadius="md" boxShadow="md" maxW="800px" mx="auto" mt={8}>
      <VStack spacing={4} align="start">
        <Heading as="h1" size="lg" color="teal.500">
          Plan de Trabajo - Presidencia
        </Heading>

        <Text fontSize="lg">
          Este es el plan de trabajo para el equipo de la presidencia, donde se detalla las tareas
          y objetivos clave a cumplir durante el próximo trimestre.
        </Text>

        <Box bg="white" p={4} borderRadius="md" boxShadow="sm" w="100%">
          <HStack justifyContent="space-between" mb={4}>
            <Text fontWeight="bold" color="gray.600">
              Periodo: Julio - Septiembre 2024
            </Text>
            <Badge colorScheme="green">Activo</Badge>
          </HStack>

          <Stack spacing={3}>
            <HStack>
              <Icon as={FaRegCalendarAlt} color="teal.500" boxSize={6} />
              <Text fontSize="md" color="gray.700">
                Fecha de inicio: 1 de Julio, 2024
              </Text>
            </HStack>

            <HStack>
              <Icon as={FaTasks} color="teal.500" boxSize={6} />
              <Text fontSize="md" color="gray.700">
                Tareas pendientes: 15
              </Text>
            </HStack>

            <HStack>
              <Icon as={FaFileAlt} color="teal.500" boxSize={6} />
              <Text fontSize="md" color="gray.700">
                Documentos asociados: 8
              </Text>
            </HStack>
          </Stack>

          <Button colorScheme="teal" mt={4} width="full" onClick={() => onOpen()}>
            Ver Gráfico de Progreso
          </Button>
        </Box>

        <Box bg="white" p={4} borderRadius="md" boxShadow="sm" w="100%">
          <Heading as="h3" size="md" color="teal.500" mb={2}>
            Objetivos Clave
          </Heading>
          <Stack spacing={3}>
            <Text fontSize="md" color="gray.700">
              - Implementar nuevas políticas de transparencia.
            </Text>
            <Text fontSize="md" color="gray.700">
              - Mejorar la infraestructura digital del municipio.
            </Text>
            <Text fontSize="md" color="gray.700">
              - Fomentar la participación ciudadana en las decisiones gubernamentales.
            </Text>
          </Stack>
        </Box>

        <Box bg="white" p={4} borderRadius="md" boxShadow="sm" w="100%">
          <Heading as="h3" size="md" color="teal.500" mb={2}>
            Responsables
          </Heading>
          <Stack spacing={3}>
            <Text fontSize="md" color="gray.700">
              - Juan Pérez - Coordinador de Proyectos
            </Text>
            <Text fontSize="md" color="gray.700">
              - María López - Asesora Legal
            </Text>
            <Text fontSize="md" color="gray.700">
              - Carlos Ramírez - Director de Tecnologías
            </Text>
          </Stack>
        </Box>

        <Box bg="white" p={4} borderRadius="md" boxShadow="sm" w="100%">
          <Heading as="h3" size="md" color="teal.500" mb={2}>
            Lista de Tareas
          </Heading>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Tarea</Th>
                <Th>Responsable</Th>
                <Th>Estado</Th>
                <Th>Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td>Desarrollar Estrategia Digital</Td>
                <Td>Carlos Ramírez</Td>
                <Td><Badge colorScheme="yellow">En Proceso</Badge></Td>
                <Td>
                  <Button size="sm" colorScheme="blue" onClick={() => handleTaskClick('Desarrollar Estrategia Digital')}>
                    Ver Detalles
                  </Button>
                </Td>
              </Tr>
              <Tr>
                <Td>Revisión de Políticas Públicas</Td>
                <Td>María López</Td>
                <Td><Badge colorScheme="green">Completado</Badge></Td>
                <Td>
                  <Button size="sm" colorScheme="blue" onClick={() => handleTaskClick('Revisión de Políticas Públicas')}>
                    Ver Detalles
                  </Button>
                </Td>
              </Tr>
              <Tr>
                <Td>Organizar Consulta Ciudadana</Td>
                <Td>Juan Pérez</Td>
                <Td><Badge colorScheme="red">Pendiente</Badge></Td>
                <Td>
                  <Button size="sm" colorScheme="blue" onClick={() => handleTaskClick('Organizar Consulta Ciudadana')}>
                    Ver Detalles
                  </Button>
                </Td>
              </Tr>
            </Tbody>
          </Table>
        </Box>

        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <HStack>
                <Icon as={FaInfoCircle} />
                <Text>Detalles de {selectedTask}</Text>
              </HStack>
            </ModalHeader>
            <ModalBody>
              <Text>Aquí puedes mostrar información detallada de la tarea seleccionada: {selectedTask}.</Text>
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="teal" mr={3} onClick={onClose}>
                Cerrar
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <HStack>
                <Icon as={FaChartLine} />
                <Text>Gráfico de Progreso</Text>
              </HStack>
            </ModalHeader>
            <ModalBody>
              <Line data={data} />
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="teal" mr={3} onClick={onClose}>
                Cerrar
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </Box>
  );
};

export default PlanDeTrabajoPresidencia;
